import { useInkind } from '@gik/api/inkinds/inkind';
import type { UseStreamSWROptions } from '@gik/api/stream/useStream';
import { timeoutDefaultValue } from '@gik/core/constants';
import { useUserStore } from '@gik/core/store/UserStore';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import commonTranslationKeys from '@gik/i18n/en/common';
import { removeUpdate } from '@gik/inkind-page/api/UserUpdatesAPI';
import { InkindUserUpdateTimelineCard } from '@gik/inkind-page/components/InkindUserUpdateTimelineCard/InkindUserUpdateTimelineCard';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import type InkindUserUpdate from '@gik/inkind-page/models/InkindUserUpdate';
import { openAddEditUpdateModal } from '@gik/inkind-page/utils/modals';
import { useInkindParticipants } from '@gik/inkind-page/utils/useInkindParticipants';
import type {
  StreamPaginationDeleteItemCallback,
  StreamPaginationReloadItemCallback,
  StreamPaginationSetErrorCallback,
  StreamPaginationUpdateItemCallback,
} from '@gik/stream/components/Posts/StreamPagination';
import { StreamPagination } from '@gik/stream/components/Posts/StreamPagination';
import type { UserUpdateActivity } from '@gik/stream/types/Stream';
import StreamClient from '@gik/stream/utils/StreamClient';
import { LoadingSpinner } from '@gik/ui/LoadingSpinner';
import { UI } from '@gik/ui/UIManager';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { scroller } from 'react-scroll';
import { mapActivityToUserUpdate, sanitizeUserUpdates } from '../InkindUserUpdates/InkindUserUpdates';

export interface IInkindUserUpdatesTimelineModalProps {
  onEditSuccess?(update: InkindUserUpdate): void;
  inkindRouteId: string;
  canEdit: boolean;
  selectedUpdate?: InkindUserUpdate;
  onDeleteUpdate?(): void;
}

export const InkindUserUpdatesTimelineModalBlockName = 'inkind-user-updates-timeline-modal';

export default function InkindUserUpdatesTimelineModal({
  onEditSuccess = noop,
  canEdit,
  selectedUpdate,
  inkindRouteId,
  onDeleteUpdate = noop,
}: IInkindUserUpdatesTimelineModalProps) {
  const bem = useBemCN(InkindUserUpdatesTimelineModalBlockName);
  const { t } = useTranslation();

  const [ready, setReady] = React.useState<boolean>(false);

  const { data: inkind } = useInkind(inkindRouteId);
  const participants = useInkindParticipants(inkindRouteId);

  const userId = useUserStore(state => state.id);
  const sanitizeData = React.useCallback((items: UserUpdateActivity[]) => sanitizeUserUpdates(items, userId), [userId]);

  const handleChange = React.useCallback(() => {
    if (ready) return;
    setTimeout(() => {
      const container = document.getElementById(InkindUserUpdatesTimelineModalBlockName);

      container?.scrollTo({ top: 0 });

      setReady(true);

      if (selectedUpdate) {
        scroller.scrollTo(`timeline-card-${selectedUpdate.id}`, {
          spy: true,
          smooth: false,
          offset: -20,
          containerId: InkindUserUpdatesTimelineModalBlockName,
        });
        return;
      }
    }, timeoutDefaultValue);
  }, [selectedUpdate, ready]);

  async function handleEdit(
    update: InkindUserUpdate,
    updateItem: StreamPaginationUpdateItemCallback,
    reloadItem: StreamPaginationReloadItemCallback,
    setError: StreamPaginationSetErrorCallback
  ) {
    await openAddEditUpdateModal({ inkindRouteId, selectedUpdate: update });

    reloadItem(update.id);
    return onEditSuccess(selectedUpdate);
  }

  async function handleDelete(
    inkindRouteId: string,
    id: string,
    deleteItem: StreamPaginationDeleteItemCallback,
    setError: StreamPaginationSetErrorCallback
  ) {
    if (
      await UI.confirm(t(translationKeys.InkindUserUpdateTimelineCardDeleteConfirmCopy).toString(), {
        okText: t(commonTranslationKeys.common.delete).toString(),
      })
    ) {
      // setDeleting(true);
      await removeUpdate(inkindRouteId, id);
      // delete item from UI
      deleteItem(id);
      onDeleteUpdate();
    }
  }

  if (!inkind) return <LoadingSpinner center />;

  return (
    <div {...bem()}>
      <div {...bem('cards')}>
        <StreamPagination<UserUpdateActivity>
          // debug
          // verbose
          order="desc"
          sanitizeData={sanitizeData}
          fetch={function useFetch(paginationOptions: UseStreamSWROptions) {
            return StreamClient.getUserUpdates(inkindRouteId, { ...paginationOptions, recentReactionsLimit: 5 });
          }}
          // fetch={function useFetch(paginationOptions: UseStreamSWROptions) {
          //   return useUserUpdatesTransformed(inkindRouteId, {
          //     ...paginationOptions,
          //   });
          // }}
          onChange={handleChange}
        >
          {({ data: updates, updateItem, reloadItem, deleteItem, setError }) => {
            return updates?.map(update => {
              return (
                <InkindUserUpdateTimelineCard
                  key={update.id}
                  onEdit={(update: InkindUserUpdate) => {
                    handleEdit(update, updateItem, reloadItem, setError);
                  }}
                  onDelete={(update: InkindUserUpdate) => handleDelete(inkind.routeId, update.id, deleteItem, setError)}
                  canEdit={canEdit}
                  onDeleteUpdate={onDeleteUpdate}
                  onReaction={() => reloadItem(update.id)}
                  inkindRouteId={inkindRouteId}
                  update={mapActivityToUserUpdate(update)}
                  debugActivity={update}
                  participants={participants}
                  updateActivity={updates.find(item => item.id === update.id)}
                />
              );
            });
          }}
        </StreamPagination>
      </div>
    </div>
  );
}
